import React, { useEffect, useMemo, useState } from 'react';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { convertTimeStampToDate } from '../Help/helper';
import {
  DocumentChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import BreadCrumb from '../BreadCrumb';
import MoonLoader from 'react-spinners/MoonLoader';


const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Job Card Pricing', to: '/procurement/jobcardpricing', component: Link, current: true },  
];


const PreQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();



  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(db, 'jobs'), where('procurementPricing', '==', 'Not Set'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const quotationsData = [];
      querySnapshot.forEach((doc) => {
        quotationsData.push({ ...doc.data(), id: doc.id });
      });
      setQuotations(quotationsData);
      setIsLoading(false);
    });
  
    return () => unsubscribe();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'Job Card No',
        accessor: 'jobCardNo', 
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName', 
      },

      {
        Header: 'Date Created',
        accessor: 'createdAt',
        Cell: ({ value }) => convertTimeStampToDate(value)
      },


      {
        Header: 'Vehicle Details',
         Cell: ({ row }) => {
          const { vehicleMake, vehicleModel, vehicleYear } = row.original;
          return `${vehicleMake} ${vehicleModel} ${vehicleYear}`;
        },
      },    
   
   

      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => navigate(`/procurement/jobcardpricing/jobcost`, { state: { job: row.original } })}
            className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transform transition-all duration-200 hover:scale-105"
          >
            Add Cost
          </button>
        ),
      }

    ],
    [navigate]
  );

  const data = useMemo(() => {
    return quotations.filter((quotation) =>
      Object.values(quotation).some((item) =>
        String(item).toLowerCase().includes(searchInput.toLowerCase())
      )
    );
  }, [quotations, searchInput]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    pageCount,
    gotoPage,
    prepareRow,
  } = useTable({ columns, data }, useFilters, useSortBy, usePagination);


  


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading job cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <DocumentChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Job Card Pricing</h1>
                <p className="text-sm text-gray-600 mt-1">Manage pricing for job cards pending cost estimation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{quotations.length}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <DocumentChartBarIcon className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-amber-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Pending Pricing</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{quotations.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Ready for Quote</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">0</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-rose-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Requires Action</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{quotations.length}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <XCircleIcon className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Search Bar */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-indigo-300" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-indigo-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all duration-200"
                placeholder="Search by job card, customer name, or vehicle..."
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full">
              <thead className="bg-gradient-to-r from-indigo-500 to-purple-600">
                {headerGroups.map((headerGroup) => {
                  const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...headerGroupProps}>
                      {headerGroup.headers.map((column) => {
                        const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                        return (
                          <th
                            key={columnKey}
                            {...columnProps}
                            className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider"
                          >
                            {column.render('Header')}
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-100">
                {page.map((row) => {
                  prepareRow(row);
                  const { key, ...rowProps } = row.getRowProps();
                  return (
                    <tr key={key} {...rowProps} className="hover:bg-indigo-50 transition-colors duration-150">
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                        return (
                          <td key={cellKey} {...cellProps} className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-700">
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex gap-2">
              <button
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {'<<'}
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {'<'}
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {'>'}
              </button>
              <button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {'>>'}
              </button>
            </div>
            <span className="text-xs text-gray-700">
              Page <strong>{pageOptions.indexOf(page) + 1}</strong> of <strong>{pageOptions.length}</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreQuotations;
